import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  useChatLoaders,
  useChatMutations,
  useChatSessions,
  useCurrentSessionId,
  useLoadingSessions,
  useSelectedAgent,
} from './chat'

interface ChatSearch {
  chat?: string
}

export interface ChatSidebarSession {
  selectSession: (id: string) => void
  clearSession: () => void
  createAndOpenSession: () => Promise<void>
  creatingSession: boolean
  createFailed: boolean
}

/**
 * Binds the chat store's current session to the `?chat=` query param:
 *   - on mount, opens whatever session the URL points at
 *   - if no session is loaded once sessions arrive, opens the most recent
 *   - exposes action helpers that write the URL when the user picks a session
 *
 * Never creates a session on its own — a chat starts only when the user
 * explicitly asks for one (New chat button / empty state).
 */
export function useChatSidebarSession(slug: string): ChatSidebarSession {
  const navigate = useNavigate()
  const sessionId = useCurrentSessionId()
  const selectedAgent = useSelectedAgent()
  const { openSession } = useChatLoaders()
  const { clearCurrentSession, createSession } = useChatMutations()

  const setChatParam = useCallback(
    (nextChat: string | null) => {
      navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          chat: nextChat ?? undefined,
        }),
        replace: true,
      })
    },
    [navigate],
  )

  const [creatingSession, setCreatingSession] = useState(false)
  const [createFailed, setCreateFailed] = useState(false)

  useHydrateSessionFromUrl(slug)
  useAutoOpenMostRecent(slug, setChatParam)

  function selectSession(nextId: string) {
    if (nextId === sessionId) return
    openSession(slug, nextId)
    setChatParam(nextId)
  }

  function clearSession() {
    clearCurrentSession()
    setChatParam(null)
  }

  // New chats inherit the sticky agent the user last picked (defaulting to
  // transactions), bound at creation and locked for the session's lifetime.
  const createAndOpenSession = useCallback(async () => {
    setCreatingSession(true)
    setCreateFailed(false)
    try {
      const session = await createSession(slug, { agent: selectedAgent })
      openSession(slug, session.id)
      setChatParam(session.id)
    } catch {
      // Surface via the empty state (with a retry button) rather than
      // throwing into button handlers.
      setCreateFailed(true)
    } finally {
      setCreatingSession(false)
    }
  }, [slug, selectedAgent, createSession, openSession, setChatParam])

  return {
    selectSession,
    clearSession,
    createAndOpenSession,
    creatingSession,
    createFailed,
  }
}

// Mount-only effect: open whatever session id appears in `?chat=` when the
// hook first runs. Subsequent URL changes are driven by user actions
// (selectSession / clearSession), so we deliberately skip them.
function useHydrateSessionFromUrl(slug: string) {
  const search = useSearch({ strict: false }) as ChatSearch
  const sessionId = useCurrentSessionId()
  const { openSession } = useChatLoaders()

  const hydratedRef = useRef(false)
  useEffect(() => {
    if (hydratedRef.current) return
    hydratedRef.current = true
    const fromUrl = typeof search?.chat === 'string' ? search.chat : null
    if (fromUrl && fromUrl !== sessionId) {
      openSession(slug, fromUrl)
    }
  }, [search, sessionId, slug, openSession])
}

// Fallback when the URL has no `?chat=` and no session is currently selected:
// once the sessions list lands, open the user's own most recent thread.
function useAutoOpenMostRecent(
  slug: string,
  setChatParam: (id: string | null) => void,
) {
  const search = useSearch({ strict: false }) as ChatSearch
  const sessionId = useCurrentSessionId()
  const sessions = useChatSessions()
  const loadingSessions = useLoadingSessions()
  const { openSession } = useChatLoaders()

  useEffect(() => {
    if (sessionId) return
    if (loadingSessions) return
    if (sessions.length === 0) return
    const fromUrl = typeof search?.chat === 'string' ? search.chat : null
    if (fromUrl) return
    // Land on the user's OWN most recent thread, never a teammate's. Chats
    // are shared per project, but each user opens into their own work. If the
    // user has no thread of their own yet, do nothing — the empty state lets
    // them start one. (Sessions are newest-first.)
    const mostRecentMine = sessions.find((session) => session.mine)
    if (!mostRecentMine) return
    openSession(slug, mostRecentMine.id)
    setChatParam(mostRecentMine.id)
  }, [
    sessionId,
    loadingSessions,
    sessions,
    slug,
    search?.chat,
    openSession,
    setChatParam,
  ])
}
